import {
  Fragment,
  cloneElement,
  isValidElement,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react'
import type { UseTableColumnsResult } from '@/hooks/useTableColumns'
import { stickyCellClass } from '@/lib/stickyColumn'

/**
 * Renders one row's cells in the column order, dropping the hidden ones.
 *
 * The alternative — guarding each `<TableCell>` with its own
 * `{cols.isVisible('x') && …}` — works, but every table then holds the column
 * order in two places (the header map and the body) and they shear apart the
 * first time someone inserts a column into one and not the other. That is a
 * misalignment you see only by eye, on a screen you may not have opened.
 *
 * Here the body is driven by the same `cols.visible` array as the header, so the
 * two cannot disagree: a cell is rendered if and only if its header is, and in
 * the same position. A column with no entry in `cells` renders an empty cell
 * rather than collapsing the row, which keeps a forgotten cell a visible gap
 * instead of a silent column shift.
 *
 *     <TableRow>
 *       <ColumnCells
 *         columns={cols}
 *         cells={{
 *           name: <TableCell className='font-medium'>{u.fullName}</TableCell>,
 *           email: <TableCell>{u.email}</TableCell>,
 *         }}
 *       />
 *     </TableRow>
 *
 * Every value is a complete `<TableCell>` (or `<td>`), not its contents, so a
 * table keeps whatever per-cell classes, colSpans and alignment it already had.
 *
 * ## Why the body cells carry the width too
 *
 * A resize used to set the width on the `<th>` alone. Under `table-layout:
 * auto` — which every table here uses — a column is as wide as its widest
 * CELL, so a narrow header with wide body content left the header text sized to
 * one box and the rows painting past it into the neighbouring column. That is
 * the overlap: the column never actually narrowed, only its header did.
 *
 * So a column with a stored width now applies it to every cell in that column,
 * with `overflow: hidden` so content clips at the boundary instead of spilling
 * over the next column's. Cells in a column nobody has resized are passed
 * through untouched, which keeps every table that has never been customized
 * pixel-identical to before.
 *
 * Note the cells map is built eagerly, so the JSX for a hidden column is
 * constructed and thrown away. That is just an unrendered element — no hook
 * runs, no effect fires, nothing mounts — but it does mean the expressions must
 * be safe to evaluate for every row, hidden or not. Anything genuinely
 * expensive belongs behind `cols.isVisible(id)` at the call site.
 */
export function ColumnCells({
  columns,
  cells,
  stickyEdge = false,
}: {
  columns: UseTableColumnsResult
  cells: Record<string, ReactNode>
  /**
   * Whether a right-pinned column currently has content hidden underneath it.
   * Comes from `useStickyColumnEdge` on the table; only tables that declare a
   * `sticky` column need to pass it.
   */
  stickyEdge?: boolean
}) {
  return (
    <>
      {columns.visible.map(({ def, width, pinned }) => (
        <Fragment key={def.id}>
          {sized(cells[def.id] ?? <td />, width, pinned, def.sticky === 'right', stickyEdge)}
        </Fragment>
      ))}
    </>
  )
}

/**
 * Apply a column's width to the cell the caller gave us.
 *
 * The cell's own `style` wins on any property it already sets — a table that
 * has deliberately pinned a cell keeps doing what it did. Its className is
 * appended to, never replaced, for the same reason.
 */
function sized(
  cell: ReactNode,
  width: number | undefined,
  pinned: boolean,
  sticky: boolean,
  stickyEdge: boolean,
): ReactNode {
  // A sticky column needs its classes whether or not anyone has resized it —
  // unlike width, which is nothing to apply when there is none.
  if ((width == null && !sticky) || !isValidElement(cell)) return cell
  const props = cell.props as { style?: CSSProperties; className?: string }
  // A DRAGGED width is an instruction: hold exactly this, clip the overflow.
  // A DEFAULT is a preference, so only `width` is set — `table-layout: auto`
  // treats that as a starting point and may compress it when the viewport is
  // narrow, which is the difference between a table that fits and one whose
  // last column sits off the right-hand edge.
  const sizing: CSSProperties =
    width == null ? {} : pinned ? { width, minWidth: width, maxWidth: width } : { width }
  const style: CSSProperties = { ...sizing, ...props.style }
  const extra = [
    // `overflow-hidden` belongs to a dragged width, not to stickiness — a
    // pinned column with no dragged width must still be allowed to size to
    // its buttons.
    width != null && pinned ? 'overflow-hidden' : null,
    sticky ? stickyCellClass('body', stickyEdge) : null,
  ].filter(Boolean)
  return cloneElement(cell as ReactElement<{ style?: CSSProperties; className?: string }>, {
    style,
    className: [props.className, ...extra].filter(Boolean).join(' ') || undefined,
  })
}
